<template>
  <component
    :is="tag"
    :class="tagClass"
  >
    <template v-if="!spacer">
      <b-form-checkbox
        v-if="isCheckbox"
      >
        {{ label }}
      </b-form-checkbox>
      <template v-else>
        <label
          :for="fieldID"
          :class="labelClass"
        >
        {{ label }}
        <v-icon
          v-if="isRequired"
          name="asterisk"
          scale="0.5"
          class="text-danger"
        />
        </label>
        <div :class="inputDivClass">
          <flat-pickr
            v-if="isDate"
            v-model="dateValue"
            :id="fieldID"
            :class="inputClass"
            :config="config"
            :placeholder="$attrs.placeholder"
            :disabled="disabled"
            @on-change="onChange"
            @on-close="onClose"
            @on-open="onOpen"
          />
          <b-form-textarea
            v-else-if="isTextarea"
            :id="fieldID"
            :readonly="isReadOnly"
            :plaintext="isPlainText"
            :no-resize="noResize"
            :class="inputClass"
            :rows="rows"
            :value="value"
            :disabled="disabled"
            :trim="trim"
            maxlength="255"
            @input="onInput"
            @blur="onBlur"
          />
          <b-form-select
            v-else-if="isSelect"
            :id="fieldID"
            :readonly="isReadOnly"
            :class="inputClass"
            :value="value"
            :size="inputSize"
            :disabled="disabled"
            :options="selectOptions"
            @input="onInput"
            @focus="onFocus"
            @change="onChange"
          >
            <template v-slot:first>
              <option
                value=""
                disabled
                v-text="$get(options, 'placeholder', '-- Please select an option --')"
              />
            </template>
          </b-form-select>
          <input
            v-else
            :type="type"
            :readonly="isReadOnly"
            :class="inputClass"
            :id="fieldID"
            :value="value"
            :size="inputSize"
            :disabled="disabled"
            maxlength="255"
            @input="onInput"
            @focus="onFocus"
            @change="onChange"
            @keypress.enter="onEnter"
          />
          <slot name="default" />
        </div>
      </template>
    </template>
  </component>
</template>
<script>
export default {
  name: 'InputField',
  props: {
    tag: {
      type: String,
      default: 'div',
    },
    label: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number],
      default: '',
    },
    spacer: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    valid: {
      type: [Boolean, Object],
      default: null,
      validator: val => [true, false, null].includes(val),
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      dateValue: null,
    };
  },
  computed: {
    dateInstance() {
      // eslint-disable-next-line no-underscore-dangle
      return document.querySelector(`#${this.fieldID}`)._flatpickr;
    },
    fieldID() {
      const id = `field_${Date.now()}_${this.type}`;
      return this.$get(this.options, 'id', id);
    },
    inputDivClass() {
      return this.$get(this.options, 'input.modifiers', '');
    },
    tagClass() {
      return this.$get(this.options, 'tagClass', 'form-group');
    },
    inputClass() {
      const fromOptions = this.$get(this.options, 'inputClass', '');
      let validClass = '';
      if (typeof this.valid === 'boolean') {
        validClass = this.valid ? 'is-valid' : 'is-invalid';
      }
      return `${fromOptions} ${validClass}`;
    },
    labelClass() {
      return this.$get(this.options, 'labelClass', '');
    },
    inputSize() {
      return this.$get(this.options, 'input.size', 'md');
    },
    rows() {
      return this.$get(this.options, 'rows', 5);
    },
    isReadOnly() {
      return this.$get(this.options, 'readonly', false);
    },
    isPlainText() {
      return this.$get(this.options, 'plaintext', false);
    },
    noResize() {
      return this.$get(this.options, 'noResize', false);
    },
    type() {
      return this.$get(this.options, 'type', 'text');
    },
    trim() {
      return this.$get(this.options, 'trim', false);
    },
    selectOptions() {
      const options = this.$get(this.options, 'selectOptions', []);

      return options.map((option) => {
        const isObj = typeof option === 'object';
        const isStr = typeof option === 'string';
        if (isObj) {
          return option;
        }
        if (isStr) {
          return { value: option, text: option };
        }
        return { value: null, text: '-' };
      });
    },
    isTextarea() {
      return this.type === 'textarea';
    },
    isCheckbox() {
      return this.type === 'checkbox';
    },
    isSelect() {
      return this.type === 'select';
    },
    isDate() {
      return this.type === 'date';
    },
    config() {
      const optionsProvided = this.$get(this.options, 'options', {});
      return Object.assign({ static: true }, optionsProvided);
    },
  },
  methods: {
    onInput(payload) {
      switch (this.type) {
        case 'select':
        case 'textarea':
          this.$emit('input', payload);
          break;
        default: {
          const data = this.$get(payload, 'target.value', '');
          this.$emit('input', data);
          break;
        }
      }
    },
    onBlur(name) {
      this.$emit('blur', name);
    },
    onFocus(name) {
      this.$emit('focus', name);
    },
    onChange(payload) {
      this.$emit('change', payload);
    },
    onClose() {
      this.$emit('dp:close', { value: this.value });
    },
    onOpen() {
      this.$emit('dp:open', { value: this.value });
    },
    onEnter() {
      this.$emit('key:enter');
    },
  },
};
</script>
